import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  OfferSelectOption,
  PlacementInfo,
  OfferSelectComponent,
  OfferInfoBoxComponent,
} from '@recruitee/offers-ui';
import type { Placement } from '@recruitee/placements-types';
import {
  RtStatefulComponent,
  ProtectedControlDirective,
  BadgeComponent,
  HeadingComponent,
  TextareaComponent,
} from '@recruitee/user-interface';
import { RedactorConfig, RedactorService, RedactorComponent } from '@recruitee/wysiwyg';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import { InterviewButtonSelectComponent } from '../interview-button-select/interview-button-select.component';

type State = {
  noteOpen: boolean;
};

@Component({
  selector: 'rt-interview-summary',
  templateUrl: 'interview-summary.component.html',
  styleUrls: ['./interview-summary.component.less'],
  standalone: true,
  imports: [
    InterviewBaseBoxComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    HeadingComponent,
    OfferInfoBoxComponent,
    OfferSelectComponent,
    BadgeComponent,
    RedactorComponent,
    ProtectedControlDirective,
    TextareaComponent,
    InterviewButtonSelectComponent,
    TranslatePipe,
  ],
})
export class InterviewSummaryComponent extends RtStatefulComponent<State> {
  @Input() public parentForm: UntypedFormGroup;
  @Input() public container: boolean = true;
  @Input() public short: boolean = false;
  @Input() public showSelectedValue: boolean = false;
  @Input() public useRedactor: boolean = false;
  @Input() public showRequiredLabel: boolean = false;
  @Input() public redactorAutofocus: boolean = true;
  @Input() public offers: OfferSelectOption[];
  @Input() public assignedPlacement?: PlacementInfo | null;
  @Input() public placements?: Placement[];
  @Input()
  public set hasOpenNote(noteOpen: boolean) {
    this.setState({
      noteOpen,
    });
  }
  @Output()
  public rateChange: EventEmitter<void> = new EventEmitter<void>();

  public readonly redactorConfig: RedactorConfig = {
    ...this.redactorSvc.defaultConfig,
    autoparseVideo: false,
    minHeight: '100px',
    maxHeight: '600px',
  };
  public readonly question: { questionTitle: string; questionRequired: boolean; summary: boolean } =
    {
      questionTitle: this.i18n.translate('app.interview.summary.title'),
      questionRequired: false,
      summary: true,
    };
  public readonly ratingMap: { [key: string]: string } = {
    strong_no: this.i18n.translate('app.interview.summary.strong_no'),
    no: this.i18n.translate('app.interview.summary.no'),
    neutral: this.i18n.translate('app.interview.summary.neutral'),
    yes: this.i18n.translate('app.interview.summary.yes'),
    strong_yes: this.i18n.translate('app.interview.summary.strong_yes'),
  };

  private ngOnDestroy$: Subject<void> = new Subject();

  constructor(
    private redactorSvc: RedactorService,
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private store: Store,
  ) {
    super(cdRef);
    this.createState({
      noteOpen: false,
    });
  }

  public openNote(): void {
    this.setState({
      noteOpen: true,
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngOnInit() {
    this.parentForm
      .get('offerId')
      ?.valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(offerId => {
        const stageId = this.placements?.find(placement => offerId === placement.offerId)?.stageId;

        if (stageId) {
          this.parentForm.get('stageId')!.setValue(stageId);
        } else {
          this.parentForm.patchValue({ stageId: null });
        }
      });
  }
}
